<template>
  <v-layout row wrap>
    <v-flex xs12 style="display: flex; flex-direction: column; height: 100%">
      <v-card
        v-if="modifiedAisConfig"
        class="pa-2"
        style="flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column"
      >
        <!-- Header -->
        <v-card-title class="pb-0 d-flex" style="flex-direction: column; align-items: stretch">
          <v-layout class="pb-0" row wrap style="flex: 0 0; align-items: flex-start; border-bottom: 1px solid #ccc">
            <span class="subheading mb-1 grow">{{ $t('Pilot.Anticollision.Configuration') }}</span>
            <v-switch
              hide-details
              class="pb-2 sm-switch"
              v-model="config.enable"
              @change="saveConfig"
              :label="$t('Pilot.Anticollision.Enable')"
              style="flex: 0 0 auto"
            />
          </v-layout>
        </v-card-title>

        <v-card-text :class="{ 'disabled-section': !config.enable }" style="flex: 1 1 0; min-height: 0; overflow: auto">
          <v-form ref="aisForm" lazy-validation>
            <!-- Zone A : cercles + rayons (zone de taille constante, centrée) -->
            <v-layout row wrap class="zone-row">
              <v-flex xs12 sm6>
                <div class="circles-wrap">
                  <svg viewBox="0 0 320 320" class="circles-svg">
                    <defs>
                      <marker id="arrowBlue" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                        <polygon points="0 0, 8 3, 0 6" fill="#4066aa" />
                      </marker>
                      <marker id="arrowRed" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                        <polygon points="0 0, 8 3, 0 6" fill="#e53e3e" />
                      </marker>
                    </defs>

                    <!-- Cercle de surveillance (externe, bleu) -->
                    <circle
                      cx="160"
                      cy="160"
                      r="140"
                      fill="none"
                      stroke="#4066aa"
                      stroke-width="2.5"
                      :class="{
                        'svg-highlight': focusedField === 'workingRadius',
                        'svg-dimmed': focusedField && focusedField !== 'workingRadius',
                      }"
                      class="svg-transition"
                    />

                    <!-- Cercle d'urgence (interne, rouge) -->
                    <circle
                      cx="160"
                      cy="160"
                      :r="innerRadius"
                      fill="none"
                      stroke="#e53e3e"
                      stroke-width="2.5"
                      :class="{
                        'svg-highlight': focusedField === 'riskRadius',
                        'svg-dimmed': focusedField && focusedField !== 'riskRadius',
                      }"
                      class="svg-transition"
                    />

                    <!-- Icône SeaExplorer centrale (le glider surveillé) — glow
                         blanc discret derrière pour la détacher des cercles. -->
                    <ellipse cx="160" cy="160" rx="42" ry="18" fill="white" opacity="0.55" />
                    <image :href="seaExplorerUrl" x="124" y="147" width="72" height="26" />

                    <!-- Flèche rayon surveillance (longueur = rayon externe) -->
                    <line
                      x1="160"
                      y1="160"
                      :x2="outerArrowEnd.x"
                      :y2="outerArrowEnd.y"
                      stroke="#4066aa"
                      stroke-width="1.5"
                      stroke-dasharray="5,3"
                      marker-end="url(#arrowBlue)"
                      :class="{ 'svg-dimmed': focusedField && focusedField !== 'workingRadius' }"
                      class="svg-transition"
                    />
                    <text
                      :x="outerArrowEnd.x - 150"
                      :y="outerArrowEnd.y + 4"
                      font-size="11"
                      fill="#4066aa"
                      font-weight="600"
                    >
                      {{ $t('Pilot.Anticollision.MonitoringRadiusValue', [config.workingRadius]) }}
                    </text>

                    <!-- Flèche rayon urgence (longueur = rayon interne) -->
                    <line
                      x1="160"
                      y1="160"
                      :x2="innerArrowEnd.x"
                      :y2="innerArrowEnd.y"
                      stroke="#e53e3e"
                      stroke-width="1.5"
                      stroke-dasharray="5,3"
                      marker-end="url(#arrowRed)"
                      :class="{ 'svg-dimmed': focusedField && focusedField !== 'riskRadius' }"
                      class="svg-transition"
                    />
                    <text
                      :x="innerArrowEnd.x - 90"
                      :y="innerArrowEnd.y + 34"
                      font-size="11"
                      fill="#e53e3e"
                      font-weight="600"
                    >
                      {{ $t('Pilot.Anticollision.EmergencyRadiusValue', [config.riskRadius]) }}
                    </text>
                  </svg>
                </div>
              </v-flex>
              <v-flex xs12 sm6 class="form-panel flex flex-col">
                <div class="form-section-title">{{ $t('Pilot.Anticollision.MonitoringRadius') }}</div>
                <v-text-field
                  type="number"
                  v-model.number="config.workingRadius"
                  :label="$t('Pilot.Anticollision.MonitoringRadius')"
                  :rules="monitoringRules"
                  @input="validateConfig"
                  @focus="focusedField = 'workingRadius'"
                  @blur="focusedField = null"
                  min="1"
                  max="50"
                  color="#4066aa"
                />
                <v-text-field
                  type="number"
                  v-model.number="config.riskRadius"
                  :label="$t('Pilot.Anticollision.EmergencyRadius')"
                  :rules="emergencyRules"
                  @input="validateConfig"
                  @focus="focusedField = 'riskRadius'"
                  @blur="focusedField = null"
                  min="0.1"
                  :max="config.workingRadius"
                  step="0.1"
                  color="#e53e3e"
                />

                <!-- Zone B : barre verticale Zt/Zb (hauteur constante) + champs à droite -->
                <div class="depth-bar-row">
                  <svg viewBox="0 0 80 200" class="depth-bar-svg">
                    <!-- Axe vertical -->
                    <line
                      x1="50"
                      y1="15"
                      x2="50"
                      y2="185"
                      stroke="#888"
                      stroke-width="1.5"
                      stroke-dasharray="4,2"
                      class="svg-transition"
                    />

                    <!-- Référence surface -->
                    <line x1="40" y1="15" x2="60" y2="15" stroke="#4a90d9" stroke-width="1" />
                    <text x="38" y="19" font-size="9" fill="#4a90d9" text-anchor="end">surface</text>

                    <!-- Marqueur Zt (top) -->
                    <line
                      x1="43"
                      y1="55"
                      x2="57"
                      y2="55"
                      stroke="#666"
                      stroke-width="2"
                      :class="{
                        'svg-highlight': focusedField === 'safetyZt',
                        'svg-dimmed': focusedField && focusedField !== 'safetyZt',
                      }"
                      class="svg-transition"
                    />
                    <text x="41" y="58" font-size="10" fill="#666" text-anchor="end" font-weight="600">Zt</text>

                    <!-- Marqueur Zb (bottom) -->
                    <line
                      x1="43"
                      y1="160"
                      x2="57"
                      y2="160"
                      stroke="#666"
                      stroke-width="2"
                      :class="{
                        'svg-highlight': focusedField === 'safetyZb',
                        'svg-dimmed': focusedField && focusedField !== 'safetyZb',
                      }"
                      class="svg-transition"
                    />
                    <text x="41" y="163" font-size="10" fill="#666" text-anchor="end" font-weight="600">Zb</text>

                    <!-- Zone sûre ombrée -->
                    <rect x="46" y="55" width="8" height="105" fill="#4066aa" opacity="0.08" rx="2" />
                  </svg>

                  <div class="depth-bar-fields">
                    <div class="form-section-title">{{ $t('Pilot.Anticollision.EmergencyTopDepth') }}</div>
                    <v-text-field
                      type="number"
                      v-model.number="config.safetyZt"
                      :label="$t('Pilot.Anticollision.EmergencyTopDepth')"
                      :rules="emergencyTopDepthRules"
                      @input="validateConfig"
                      @focus="focusedField = 'safetyZt'"
                      @blur="focusedField = null"
                      min="2"
                      max="20"
                    />
                    <v-text-field
                      type="number"
                      v-model.number="config.safetyZb"
                      :label="$t('Pilot.Anticollision.EmergencyBottomDepth')"
                      :rules="emergencyBottomDepthRules"
                      @input="validateConfig"
                      @focus="focusedField = 'safetyZb'"
                      @blur="focusedField = null"
                      min="30"
                      max="1000"
                    />
                  </div>
                </div>
              </v-flex>
            </v-layout>

            <!-- Zone C : projection période — SVG gauche (extensible), champs droite -->
            <v-layout row wrap class="zone-row">
              <v-flex xs12 sm7 class="my-auto">
                <div ref="projectionContainer" class="projection-wrap">
                  <svg :viewBox="`0 0 ${projectionWidth} 130`" class="projection-svg">
                    <!-- Barre timeline -->
                    <rect
                      x="30"
                      y="55"
                      :width="Math.max(0, projectionWidth - 60)"
                      height="6"
                      rx="3"
                      fill="#4066aa"
                      :class="{
                        'svg-highlight': focusedField === 'projectionDuration',
                        'svg-dimmed':
                          focusedField && focusedField !== 'projectionDuration' && focusedField !== 'projectionPeriod',
                      }"
                      class="svg-transition"
                    />

                    <!-- Bateau projeté 1 (départ) — icône ferry blanche dans le cercle bleu -->
                    <g transform="translate(55,42)">
                      <circle cx="0" cy="0" r="14" fill="#4066aa" />
                      <path :d="ferryPath" transform="translate(-9,-9) scale(0.75)" fill="white" opacity="0.9" />
                    </g>

                    <!-- Bateau projeté 2 (repère période) -->
                    <g :transform="`translate(${periodMarkerX},42)`">
                      <circle cx="0" cy="0" r="14" fill="#4066aa" opacity="0.55" />
                      <path :d="ferryPath" transform="translate(-9,-9) scale(0.75)" fill="white" opacity="0.7" />
                    </g>

                    <!-- Bateau projeté 3 (fin durée) -->
                    <g :transform="`translate(${durationMarkerX},42)`">
                      <circle cx="0" cy="0" r="14" fill="#4066aa" opacity="0.3" />
                      <path :d="ferryPath" transform="translate(-9,-9) scale(0.75)" fill="white" opacity="0.5" />
                    </g>

                    <!-- Bracket période (haut) -->
                    <line
                      x1="55"
                      y1="18"
                      :x2="periodMarkerX"
                      y2="18"
                      stroke="#4066aa"
                      stroke-width="1.5"
                      :class="{
                        'svg-highlight': focusedField === 'projectionPeriod',
                        'svg-dimmed': focusedField && focusedField !== 'projectionPeriod',
                      }"
                      class="svg-transition"
                    />
                    <line x1="55" y1="14" x2="55" y2="22" stroke="#4066aa" stroke-width="1.5" class="svg-transition" />
                    <line
                      :x1="periodMarkerX"
                      y1="14"
                      :x2="periodMarkerX"
                      y2="22"
                      stroke="#4066aa"
                      stroke-width="1.5"
                      class="svg-transition"
                    />
                    <text
                      :x="(55 + periodMarkerX) / 2"
                      y="12"
                      font-size="10"
                      fill="#4066aa"
                      font-weight="600"
                      text-anchor="middle"
                    >
                      {{ $t('Pilot.Anticollision.PeriodProjectionValue', [config.projectionPeriod]) }}
                    </text>

                    <!-- Bracket durée (bas) -->
                    <line
                      x1="55"
                      y1="78"
                      :x2="durationMarkerX"
                      y2="78"
                      stroke="#4066aa"
                      stroke-width="1.5"
                      :class="{
                        'svg-highlight': focusedField === 'projectionDuration',
                        'svg-dimmed': focusedField && focusedField !== 'projectionDuration',
                      }"
                      class="svg-transition"
                    />
                    <line x1="55" y1="74" x2="55" y2="82" stroke="#4066aa" stroke-width="1.5" class="svg-transition" />
                    <line
                      :x1="durationMarkerX"
                      y1="74"
                      :x2="durationMarkerX"
                      y2="82"
                      stroke="#4066aa"
                      stroke-width="1.5"
                      class="svg-transition"
                    />
                    <text
                      :x="(55 + durationMarkerX) / 2"
                      y="100"
                      font-size="10"
                      fill="#4066aa"
                      font-weight="600"
                      text-anchor="middle"
                    >
                      {{ $t('Pilot.Anticollision.DurationProjectionValue', [config.projectionDuration]) }}
                    </text>
                  </svg>
                </div>
              </v-flex>
              <v-flex xs12 sm5 class="form-panel flex flex-col">
                <div class="form-section-title">{{ $t('Pilot.Anticollision.PeriodProjection') }}</div>
                <v-text-field
                  type="number"
                  v-model.number="config.projectionPeriod"
                  :label="$t('Pilot.Anticollision.PeriodProjection')"
                  :rules="periodProjectionRules"
                  @input="validateConfig"
                  @focus="focusedField = 'projectionPeriod'"
                  @blur="focusedField = null"
                  min="1"
                  max="30"
                  color="#4066aa"
                />
                <div class="form-section-title mt-3">{{ $t('Pilot.Anticollision.DurationProjection') }}</div>
                <v-text-field
                  type="number"
                  v-model.number="config.projectionDuration"
                  :label="$t('Pilot.Anticollision.DurationProjection')"
                  :rules="durationProjectionRules"
                  @input="validateConfig"
                  @focus="focusedField = 'projectionDuration'"
                  @blur="focusedField = null"
                  min="30"
                  max="720"
                  color="#4066aa"
                />
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click.prevent="restoreConfig" :disabled="!hasChanges || aisLoading" :loading="aisLoading" small>
            {{ $t('Pilot.Anticollision.RestoreConfig') }}
            <v-icon small right>mdi-replay</v-icon>
          </v-btn>
          <v-btn @click.prevent="saveConfig" :disabled="!hasChanges || aisLoading" small color="primary">
            {{ $t('Pilot.Anticollision.ValidateConfig') }}
            <v-icon small right>mdi-content-save</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else class="pa-2">
        <v-card-text>{{ $t('Pilot.Anticollision.NoDataAvailable') }}</v-card-text>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script lang="ts">
import { AloMission } from '@/models/alo/AloMission'
import { IrisConfigContainer } from '@/models/iris/IrisConfigContainer'
import { IAisConfig } from '@/models/iris/ais/IAisConfig'
import Vue from 'vue'
import Component from 'vue-class-component'
//@ts-ignore
import deepEqual from 'deep-equal'
import { VuetifyConfirmObject } from 'vuetify-confirm'

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// Géométrie du SVG cercles (320x320, centre 160/160, rayon externe 140)
const CIRCLES_CX = 160
const CIRCLES_CY = 160
const CIRCLES_OUTER_R = 140
const OUTER_ANGLE = -Math.PI / 4 // -45° (haut-droit)
const INNER_ANGLE = Math.PI / 6 // +30° (bas-droit) pour éviter le chevauchement des labels

@Component({
  name: 'MissionDetailAnticollision',
  props: {
    mission: {
      type: AloMission,
    },
  },
})
export default class MissionDetailAnticollision extends Vue {
  mission!: AloMission

  focusedField: string | null = null
  projectionWidth = 420

  // Icône SeaExplorer centrale (asset bundlé webpack, comme arrow.svg dans MapStylesFactory)
  seaExplorerUrl: string = require('@/assets/seaexplorer.svg')

  // Tracé icône bateau (mdi-ferry, viewBox 24×24) — même icône que les alertes
  // anticollision (MissionState.vue). Utilisée pour les bateaux projetés (timeline).
  ferryPath =
    'M6,6H18V9.96L12,8L6,9.96M3.94,19H4C5.6,19 7,18.12 8,17C9,18.12 10.4,19 12,19C13.6,19 15,18.12 16,17C17,18.12 18.4,19 20,19H20.05L21.95,12.31C22.03,12.06 22,11.78 21.89,11.54C21.76,11.3 21.55,11.12 21.29,11.04L20,10.62V6C20,4.89 19.1,4 18,4H15V1H9V4H6A2,2 0 0,0 4,6V10.62L2.71,11.04C2.45,11.12 2.24,11.3 2.11,11.54C2,11.78 1.97,12.06 2.05,12.31M20,21C18.61,21 17.22,20.53 16,19.67C13.56,21.38 10.44,21.38 8,19.67C6.78,20.53 5.39,21 4,21H2V23H4C5.37,23 6.74,22.65 8,22C10.5,23.3 13.5,23.3 16,22C17.26,22.65 18.62,23 20,23H22V21H20Z'

  private projectionObserver: ResizeObserver | null = null

  config = {
    enable: false,
    workingRadius: 10,
    riskRadius: 0.5,
    safetyZb: 50,
    safetyZt: 2,
    projectionPeriod: 5,
    projectionDuration: 60,
  }

  get originalAisConfig(): IrisConfigContainer<IAisConfig> | undefined {
    return this.$store.myGetters.originalAisConfigs[this.mission.misId!]
  }

  get modifiedAisConfig(): IrisConfigContainer<IAisConfig> | undefined {
    return this.$store.myGetters.modifiedAisConfigs[this.mission.misId!]
  }

  get moduleStatus(): string | undefined {
    return this.originalAisConfig?.mod_settings?.ANTICOLL?.module_status
  }

  get aisLoading(): boolean {
    return this.$store.myGetters.aisLoading
  }

  get hasChanges(): boolean {
    return !deepEqual(this.originalAisConfig, this.modifiedAisConfig)
  }

  get innerRadius(): number {
    return Math.max(20, CIRCLES_OUTER_R * (this.config.riskRadius / this.config.workingRadius))
  }

  get outerArrowEnd(): { x: number; y: number } {
    return {
      x: CIRCLES_CX + CIRCLES_OUTER_R * Math.cos(OUTER_ANGLE),
      y: CIRCLES_CY + CIRCLES_OUTER_R * Math.sin(OUTER_ANGLE),
    }
  }

  get innerArrowEnd(): { x: number; y: number } {
    return {
      x: CIRCLES_CX + this.innerRadius * Math.cos(INNER_ANGLE),
      y: CIRCLES_CY + this.innerRadius * Math.sin(INNER_ANGLE),
    }
  }

  get durationMarkerX(): number {
    return Math.max(80, this.projectionWidth - 55)
  }

  get periodMarkerX(): number {
    const start = 55
    const end = this.durationMarkerX
    const ratio = this.config.projectionDuration > 0 ? this.config.projectionPeriod / this.config.projectionDuration : 0
    return start + (end - start) * Math.min(1, Math.max(0, ratio))
  }

  get monitoringRules() {
    return [
      (v: number) => v >= 1 || this.$t('Pilot.Anticollision.RuleMin', ['1', 'km']),
      (v: number) => v <= 50 || this.$t('Pilot.Anticollision.RuleMax', ['50', 'km']),
      (v: number) => v > this.config.riskRadius || this.$t('Pilot.Anticollision.RuleMonitoringGreater'),
    ]
  }

  get emergencyRules() {
    return [
      (v: number) => v >= 0.1 || this.$t('Pilot.Anticollision.RuleMin', ['0.1', 'km']),
      (v: number) => v < this.config.workingRadius || this.$t('Pilot.Anticollision.RuleEmergencyLess'),
    ]
  }

  get emergencyBottomDepthRules() {
    return [
      (v: number) => v >= 30 || this.$t('Pilot.Anticollision.RuleMin', ['30', 'm']),
      (v: number) => v <= 1000 || this.$t('Pilot.Anticollision.RuleMax', ['1000', 'm']),
      (v: number) => v > this.config.safetyZt || this.$t('Pilot.Anticollision.RuleZbGreaterZt'),
    ]
  }

  get emergencyTopDepthRules() {
    return [
      (v: number) => v >= 2 || this.$t('Pilot.Anticollision.RuleMin', ['2', 'm']),
      (v: number) => v <= 20 || this.$t('Pilot.Anticollision.RuleMax', ['20', 'm']),
      (v: number) => v < this.config.safetyZb || this.$t('Pilot.Anticollision.RuleZtLessZb'),
    ]
  }

  get periodProjectionRules() {
    return [
      (v: number) => v >= 1 || this.$t('Pilot.Anticollision.RuleMin', ['1', 'min']),
      (v: number) => v <= 30 || this.$t('Pilot.Anticollision.RuleMax', ['30', 'min']),
    ]
  }

  get durationProjectionRules() {
    return [
      (v: number) => v >= 30 || this.$t('Pilot.Anticollision.RuleMin', ['30', 'min']),
      (v: number) => v <= 720 || this.$t('Pilot.Anticollision.RuleMax', ['720', 'min']),
    ]
  }

  created() {
    if (this.mission.misId != null) {
      this.$store.myActions
        .MISSION_IRIS_AIS_REQUEST(this.mission.misId)
        .then(() => this.loadConfig(this.config, this.modifiedAisConfig))
    }
  }

  mounted() {
    this.$nextTick(() => this.trySetupProjectionObserver())
  }

  updated() {
    this.trySetupProjectionObserver()
  }

  beforeDestroy() {
    this.projectionObserver?.disconnect()
    this.projectionObserver = null
  }

  private trySetupProjectionObserver() {
    if (this.projectionObserver) return
    const el = this.$refs.projectionContainer as HTMLElement | undefined
    if (!el) return
    this.updateProjectionWidth()
    if (typeof ResizeObserver !== 'undefined') {
      this.projectionObserver = new ResizeObserver(() => this.updateProjectionWidth())
      this.projectionObserver.observe(el)
    }
  }

  private updateProjectionWidth() {
    const el = this.$refs.projectionContainer as HTMLElement | undefined
    if (el && el.clientWidth > 0) this.projectionWidth = el.clientWidth
  }

  loadConfig(target: typeof this.config, config: IrisConfigContainer<IAisConfig> | undefined) {
    if (config) {
      target.enable = config.mod_settings.EnabledMOD['module-AIS']
      target.workingRadius = config.mod_settings.AIS.working_radius
      target.riskRadius = config.mod_settings.AIS.risk_radius
      target.safetyZb = config.mod_settings.AIS.safety_zb
      target.safetyZt = config.mod_settings.AIS.safety_zt
      target.projectionPeriod = config.mod_settings.AIS.projection_period
      target.projectionDuration = config.mod_settings.AIS.projection_duration
    }
  }

  validateConfig() {
    this.$store.myActions.MISSION_UPDATE_MODIFIED_AIS({
      itemId: this.mission.misId!,
      newProperties: {
        mod_settings: {
          EnabledMOD: { 'module-AIS': this.config.enable },
          AIS: {
            working_radius: this.config.workingRadius,
            risk_radius: this.config.riskRadius,
            safety_zb: this.config.safetyZb,
            safety_zt: this.config.safetyZt,
            projection_period: this.config.projectionPeriod,
            projection_duration: this.config.projectionDuration,
          },
        },
      },
    })
  }

  get formErrors(): string[] {
    const fields: { label: string; value: any; rules: Array<(v: any) => true | string> }[] = [
      {
        label: this.$t('Pilot.Anticollision.MonitoringRadius') as string,
        value: this.config.workingRadius,
        rules: this.monitoringRules as any,
      },
      {
        label: this.$t('Pilot.Anticollision.EmergencyRadius') as string,
        value: this.config.riskRadius,
        rules: this.emergencyRules as any,
      },
      {
        label: this.$t('Pilot.Anticollision.EmergencyTopDepth') as string,
        value: this.config.safetyZt,
        rules: this.emergencyTopDepthRules as any,
      },
      {
        label: this.$t('Pilot.Anticollision.EmergencyBottomDepth') as string,
        value: this.config.safetyZb,
        rules: this.emergencyBottomDepthRules as any,
      },
      {
        label: this.$t('Pilot.Anticollision.PeriodProjection') as string,
        value: this.config.projectionPeriod,
        rules: this.periodProjectionRules as any,
      },
      {
        label: this.$t('Pilot.Anticollision.DurationProjection') as string,
        value: this.config.projectionDuration,
        rules: this.durationProjectionRules as any,
      },
    ]
    const errors: string[] = []
    for (const f of fields) {
      for (const rule of f.rules) {
        const r = rule(f.value)
        if (r !== true) errors.push(`${f.label} : ${r}`)
      }
    }
    return errors
  }

  saveConfig() {
    if (this.config.enable) {
      ;(this.$refs.aisForm as any)?.validate?.()
      const errors = this.formErrors
      if (errors.length) {
        const body = `<p>${escapeHtml(this.$t('Pilot.Anticollision.SaveErrorIntro') as string)}</p><ul><li>${errors
          .map(e => escapeHtml(e))
          .join('</li><li>')}</li></ul>`
        this.$confirm(body, {
          title: this.$t('Pilot.Anticollision.SaveErrorTitle') as string,
          buttonTrueText: 'Ok',
          buttonFalseText: '',
        } as VuetifyConfirmObject)
        return
      }
    }
    this.validateConfig()
    this.$store.myActions.MISSION_IRIS_AIS_SAVE_REQUEST(this.mission.misId!)
  }

  restoreConfig() {
    this.$store.myActions
      .MISSION_CANCEL_MODIFIED_AIS(this.mission.misId!)
      .then(() => this.loadConfig(this.config, this.modifiedAisConfig))
  }
}
</script>

<style scoped>
.disabled-section {
  opacity: 0.5;
  pointer-events: none;
}

.zone-row {
  align-items: flex-start;
}

.form-panel {
  padding: 8px 16px;
}

.form-section-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 4px;
}

/* Zone A : cercles — hauteur et largeur constantes, centré */
.circles-wrap {
  width: 400px;
  height: 400px;
  max-width: 100%;
  margin: 0 auto;
}
.circles-svg {
  width: 100%;
  height: 100%;
  display: block;
}

/* Zone B : barre Zt/Zb — hauteur fixe, fields à droite */
.depth-bar-row {
  display: flex;
  align-items: flex-start;
  gap: 0px;
  margin-top: 12px;
}
.depth-bar-svg {
  width: 80px;
  height: 200px;
  flex: 0 0 auto;
  display: block;
}
.depth-bar-fields {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 0px;
}

/* Zone C : projection période — hauteur constante, largeur extensible */
.projection-wrap {
  width: 100%;
}
.projection-svg {
  width: 100%;
  height: 130px;
  display: block;
}

.svg-transition {
  transition: stroke-width 0.2s ease, opacity 0.2s ease, fill-opacity 0.2s ease;
}

.svg-highlight {
  stroke-width: 4 !important;
  opacity: 1 !important;
}

.svg-dimmed {
  opacity: 0.3 !important;
}
</style>
